import * as React from "react"
import { ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shadcn/components/ui/collapsible"

/**
 * The report's one disclosure control, so every twist-out in it animates,
 * indents and reads the same. Replaces the native <details>/<summary> pairs the
 * report used previously — those couldn't be styled consistently across the
 * three nesting depths, and gave no way to change the trigger label with state.
 *
 * `label` is used for both states unless `openLabel` is given.
 */
export default function Disclosure({
  label,
  openLabel,
  defaultOpen = false,
  className,
  contentClassName,
  children,
}: {
  label: string
  openLabel?: string
  defaultOpen?: boolean
  className?: string
  contentClassName?: string
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <CollapsibleTrigger
        className={cn(
          "tw:group tw:flex tw:w-full tw:items-center tw:gap-1.5 tw:rounded-md tw:py-1 tw:text-left",
          "tw:text-sm tw:font-medium tw:text-muted-foreground",
          "tw:hover:text-foreground tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:focus-visible:outline-none"
        )}
      >
        <ChevronRight
          aria-hidden="true"
          className="tw:size-3.5 tw:shrink-0 tw:transition-transform tw:duration-150 tw:group-data-[state=open]:rotate-90"
        />
        <span>{open ? (openLabel ?? label) : label}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn("tw:pt-2", contentClassName)}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
